
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { fastapi } from '../services/api';
import { getStripeStatus, saveStripeSecret, clearStripeSecret, toggleStripeActive, type StripeStatus } from '../services/pagosConfigService';
import { useTranslation } from 'react-i18next';
// Stripe Connect onboarding services
import { startConnectOnboarding, getConnectStatus, createExpressLoginLink } from '../services/stripeConnectService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faStripeS, faStripe } from '@fortawesome/free-brands-svg-icons';
import { faExchangeAlt, faUnlink, faSync, faExternalLinkAlt, faTrash, faSave, faTimes, faLink, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import authService from '../services/authService';
import { GhostSwitch } from '../components/ui/GhostSwitch';
import BtnTaratic from '../components/ui/BtnTaratic';
import { useAppDispatch } from '../store/hooks';
import { uiActions } from '../store/ui/uiSlice';

const ConnectionsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [igInfo, setIgInfo] = useState<{ username: string | null; avatar: string | null } | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null);
  const [stripeSecret, setStripeSecret] = useState('');
  const [savingStripe, setSavingStripe] = useState(false);
  const [togglingStripe, setTogglingStripe] = useState(false);
  const [showStripeChange, setShowStripeChange] = useState(false);
  const [disconnectingStripe, setDisconnectingStripe] = useState(false);
  const [disconnectingIg, setDisconnectingIg] = useState(false);
  const [enabledFeatures, setEnabledFeatures] = useState<string[] | null>(null);
  
  const [is2faEnabled, setIs2faEnabled] = useState(false);
  const [toggling2fa, setToggling2fa] = useState(false);

  useEffect(() => {
    dispatch(uiActions.setSettingsUnsavedChanges(stripeSecret.trim().length > 0));
  }, [dispatch, stripeSecret]);

  useEffect(() => () => {
    dispatch(uiActions.setSettingsUnsavedChanges(false));
  }, [dispatch]);
  
  useEffect(() => {
    // Clean up Facebook's #_=_ hash if present
    if (window.location.hash === '#_=_') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    const success = searchParams.get('success');
    if (success === 'true') {
      setMessage({ type: 'success', text: t('connectionsPage.messages.igConnectedSuccess') });
    } else if (success === 'false') {
      setMessage({ type: 'error', text: t('connectionsPage.messages.igConnectedError') });
    }

    // Fetch IG connection status
    const fetchIgStatus = async () => {
      try {
        const comercioKey = localStorage.getItem('comercio_key');
        const params = comercioKey ? `?comercio_key=${encodeURIComponent(comercioKey)}` : '';
        const { data } = await api.get(`/instagram/connection-status${params}`);
        if (data.connected) {
          setIgInfo({ username: data.ig_username, avatar: data.profile_picture_url || null });
        }
      } catch (e) {
        console.warn('No se pudo obtener el estado de la conexión de Instagram:', e);
      }
    };

    // Fetch Stripe status
    const fetchStripe = async () => {
      try {
        const comercioKey = localStorage.getItem('comercio_key');
        if (!comercioKey) return;
        const status = await getStripeStatus(comercioKey);
        setStripeStatus(status);
      } catch (e) {
        console.warn('No se pudo obtener el estado de Stripe:', e);
      }
    };

    // If returned from Stripe onboarding, refresh Connect status
    const refreshConnectStatusIfNeeded = async () => {
      try {
        const returned = searchParams.get('stripe_return') === '1' || searchParams.get('stripe_refresh') === '1';
        const comercioKey = localStorage.getItem('comercio_key');
        if (!returned || !comercioKey) return;
        const status = await getConnectStatus(comercioKey);
        setStripeStatus((prev) => ({
          connected: !!status.connected,
          active: prev?.active ?? false,
          account: status.account
            ? {
                id: status.account.id,
                email: null,
                business_name: (status.account.business_profile && (status.account.business_profile.name as string)) || null,
              }
            : null,
          mode: undefined,
        }));
        setMessage({ type: 'success', text: t('connectionsPage.messages.stripeStatusUpdated', 'Estado de Stripe actualizado correctamente.') });
      } catch (e) {
        console.warn('No se pudo actualizar el estado de Stripe Connect:', e);
      }
    };

    const fetch2FA = async () => {
      try {
        const data = await authService.get2FASettings();
        setIs2faEnabled(data.enabled);
      } catch (e) {
        console.warn('Failed to fetch 2FA settings', e);
      }
    };

    const fetchFeatures = async () => {
      try {
        const res = await fastapi.get('/tenants/me/features');
        setEnabledFeatures(res.data);
      } catch (e) {
        console.error('Failed to fetch features', e);
        setEnabledFeatures([]);
      }
    };

    fetchIgStatus();
    fetchStripe();
    refreshConnectStatusIfNeeded();
    fetch2FA();
    fetchFeatures();
  }, [searchParams]);

  const handleToggle2FA = async () => {
    try {
      setToggling2fa(true);
      const newState = !is2faEnabled;
      await authService.update2FASettings(newState);
      setIs2faEnabled(newState);
      const statusText = newState
        ? t('connectionsPage.common.activated', 'activada')
        : t('connectionsPage.common.deactivated', 'desactivada');
      setMessage({
        type: 'success',
        text: t('connectionsPage.messages.2faToggled', {
          defaultValue: 'Doble autenticación (2FA) {{status}} correctamente.',
          status: statusText,
        }),
      });
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || t('connectionsPage.errors.update2fa', 'Error al actualizar la configuración de 2FA') });
    } finally {
      setToggling2fa(false);
    }
  };

  const handleToggleStripeActive = async () => {
    const comercioKey = localStorage.getItem('comercio_key');
    if (!comercioKey) {
      setMessage({ type: 'error', text: t('connectionsPage.errors.noCommerceKeyLocalStorage') });
      return;
    }

    try {
      setTogglingStripe(true);
      const targetState = !stripeStatus?.active;
      const res = await toggleStripeActive(comercioKey, targetState);
      setStripeStatus((prev) => (prev ? { ...prev, active: res.active } : { connected: true, active: res.active }));
      const statusText = res.active
        ? t('connectionsPage.common.activated', 'activado')
        : t('connectionsPage.common.deactivated', 'desactivado');
      setMessage({
        type: 'success',
        text: t('connectionsPage.messages.stripeToggled', {
          defaultValue: 'Stripe {{status}} correctamente.',
          status: statusText,
        }),
      });
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || t('connectionsPage.errors.toggleStripe', 'Error al cambiar el estado de Stripe.') });
    } finally {
      setTogglingStripe(false);
    }
  };

  const handleConnectInstagram = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token || token === 'null' || token.trim() === '') {
      setMessage({
        type: 'error',
        text: t('connectionsPage.errors.authNoToken'),
      });
      return;
    }
    try {
      const { data } = await fastapi.get<{ authorization_url: string }>('/auth/instagram/connect-url');
      if (!data.authorization_url) throw new Error('Missing Instagram authorization URL');
      window.location.assign(data.authorization_url);
    } catch (e: any) {
      setMessage({
        type: 'error',
        text: e?.response?.data?.detail || t('connectionsPage.messages.igConnectedError'),
      });
    }
  };

  const handleDisconnectInstagram = async () => {
    try {
      setDisconnectingIg(true);
      const comercioKey = localStorage.getItem('comercio_key') || undefined;
      await api.delete('/instagram/disconnect', { params: { comercio_key: comercioKey } });
      setIgInfo(null);
      setMessage({ type: 'success', text: t('connectionsPage.messages.igDisconnected') });
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || t('connectionsPage.messages.igDisconnectFailed') });
    } finally {
      setDisconnectingIg(false);
    }
  };

  const handleSaveStripe = async () => {
    const comercioKey = localStorage.getItem('comercio_key');
    if (!comercioKey) {
      setMessage({ type: 'error', text: t('connectionsPage.errors.noCommerceKeyLocalStorage') });
      return;
    }
    const sanitized = stripeSecret.trim();
    if (!sanitized || !/^(sk_|rk_)/i.test(sanitized)) {
      setMessage({ type: 'error', text: t('connectionsPage.errors.invalidStripeKey') });
      return;
    }
    try {
      setSavingStripe(true);
      const resp = await saveStripeSecret(comercioKey, sanitized);
      setStripeStatus(resp);
      setStripeSecret('');
      setShowStripeChange(false);
      setMessage({ type: 'success', text: t('connectionsPage.messages.stripeSavedDisabledDefault', 'Clave de Stripe guardada. Stripe permanece desactivado por defecto hasta que lo actives.') });
    } catch (e: any) {
      const be = e?.response?.data;
      const msg = be?.message || t('connectionsPage.messages.stripeSaveFailed');
      const diag = be?.stripe_error
        ? ` (${[be.stripe_error.type, be.stripe_error.code, be.stripe_error.statusCode, be.stripe_error.requestId].filter(Boolean).join(' / ')})`
        : '';
      setMessage({ type: 'error', text: `${msg}${diag}` });
    } finally {
      setSavingStripe(false);
    }
  };

  const handleConnectWithStripe = async () => {
    try {
      const comercioKey = localStorage.getItem('comercio_key');
      if (!comercioKey) {
        setMessage({ type: 'error', text: t('connectionsPage.errors.noCommerceKeyLocalStorage') });
        return;
      }
      const { url } = await startConnectOnboarding(comercioKey);
      window.location.href = url;
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || t('connectionsPage.errors.stripeOnboardingStart', 'Error al iniciar la conexión con Stripe') });
    }
  };

  const handleCheckConnectStatus = async () => {
    try {
      const comercioKey = localStorage.getItem('comercio_key');
      if (!comercioKey) {
        setMessage({ type: 'error', text: t('connectionsPage.errors.noCommerceKeyLocalStorage') });
        return;
      }
      const status = await getConnectStatus(comercioKey);
      setStripeStatus((prev) => ({
        connected: !!status.connected,
        active: prev?.active ?? false,
        account: status.account
          ? {
              id: status.account.id,
              email: null,
              business_name: (status.account.business_profile && (status.account.business_profile.name as string)) || null,
            }
          : null,
        mode: undefined,
      }));
      setMessage({ type: 'success', text: t('connectionsPage.messages.stripeStatusUpdatedShort', 'Estado de Stripe actualizado.') });
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || t('connectionsPage.errors.stripeStatusRetrieve', 'Error al obtener el estado de Stripe') });
    }
  };

  const handleOpenStripeDashboard = async () => {
    try {
      const comercioKey = localStorage.getItem('comercio_key');
      if (!comercioKey) {
        setMessage({ type: 'error', text: t('connectionsPage.errors.noCommerceKeyLocalStorage') });
        return;
      }
      const popup = window.open('about:blank', '_blank');
      try {
        const { url } = await createExpressLoginLink(comercioKey);
        if (popup) {
          popup.location.href = url;
        } else {
          window.location.href = url;
        }
      } catch (err: any) {
        if (popup) popup.close();
        throw err;
      }
    } catch (e: any) {
      const beError = e?.response?.data?.error;
      const msg = e?.response?.data?.message || t('connectionsPage.errors.stripeLoginLink', 'Error al crear el enlace de acceso a Stripe');
      setMessage({ type: 'error', text: beError ? `${msg}: ${beError}` : msg });
    }
  };

  const handleClearStripe = async () => {
    const comercioKey = localStorage.getItem('comercio_key');
    if (!comercioKey) {
      setMessage({ type: 'error', text: t('connectionsPage.errors.noCommerceKeyLocalStorage') });
      return;
    }
    try {
      setDisconnectingStripe(true);
      await clearStripeSecret(comercioKey);
      setStripeStatus({ connected: false, active: false });
      setShowStripeChange(false);
      setStripeSecret('');
      setMessage({ type: 'success', text: t('connectionsPage.messages.stripeCleared') });
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || t('connectionsPage.messages.stripeClearFailed') });
    } finally {
      setDisconnectingStripe(false);
    }
  };

  return (
    <div data-ui="settings-connections-page" className="space-y-8 animate-fade-in-up">
      {/* Page Header */}
      <div data-ui="connections-page-region-001">
        <h1 className="text-3xl font-black text-white">{t('connectionsPage.title', 'Conexiones')}</h1>
        <p className="text-white/50 mt-1">{t('connectionsPage.subtitle', 'Gestiona las pasarelas de pago, redes sociales y seguridad del comercio.')}</p>
      </div>

      {message && (
        <div data-ui="connections-page-region-002" className={`p-4 rounded-xl border backdrop-blur-md ${message.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-300' : 'bg-red-500/10 border-red-500/20 text-red-300'}`}>
          {message.text}
        </div>
      )}

      {/* Instagram Integration Card */}
      <div data-ui="connections-page-region-003" className="bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl p-6 shadow-inner-glow shadow-inner-glow-black-md border-none space-y-4">
        <div data-ui="connections-page-region-004" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div data-ui="connections-page-region-005" className="flex items-center gap-4 min-w-0 flex-1">
            <div data-ui="connections-page-region-006" className="w-12 h-12 rounded-xl bg-pink-500/20 text-pink-400 flex items-center justify-center text-2xl shrink-0 border-none shadow-inner-glow shadow-inner-glow-white-sm">
              <FontAwesomeIcon icon={faInstagram} className="icon-inner-glow-white" />
            </div>
            <div data-ui="connections-page-region-007" className="flex items-center gap-3">
              {igInfo?.avatar && (
                <img src={igInfo.avatar} alt="IG avatar" className="w-10 h-10 rounded-full object-cover border border-white/10" />
              )}
              <div data-ui="connections-page-region-008">
                <h2 className="text-lg font-bold text-white">Instagram</h2>
                <p className="text-xs text-white/80">
                  {igInfo?.username ? (
                    <span>{t('connectionsPage.instagram.connectedAs', { username: igInfo.username || '' })}</span>
                  ) : (
                    t('connectionsPage.instagram.connectHint')
                  )}
                </p>
              </div>
            </div>
          </div>
          <div data-ui="connections-page-region-009" className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full sm:w-auto sm:justify-end mt-3 sm:mt-0">
            <BtnTaratic
              data-tour="connect-instagram"
              onClick={handleConnectInstagram}
              className="!bg-pink-600 hover:!bg-pink-700 text-white flex items-center gap-2 text-sm"
              title={igInfo?.username ? t('connectionsPage.instagram.changeAccountTitle') : t('connectionsPage.instagram.connectTitle')}
            >
              <FontAwesomeIcon icon={igInfo?.username ? faExchangeAlt : faInstagram} />
              <span>{igInfo?.username ? t('connectionsPage.instagram.changeAccount') : t('connectionsPage.instagram.connect')}</span>
            </BtnTaratic>
            {igInfo?.username && (
              <BtnTaratic
                color="red"
                onClick={handleDisconnectInstagram}
                disabled={disconnectingIg}
                className="flex items-center gap-2 text-sm"
                title={t('connectionsPage.instagram.disconnectTitle')}
              >
                <FontAwesomeIcon icon={faUnlink} />
                <span>{disconnectingIg ? t('connectionsPage.common.removing') : t('connectionsPage.common.disconnect')}</span>
              </BtnTaratic>
            )}
          </div>
        </div>
      </div>

      {/* Stripe Payment Gateway Card */}
      {enabledFeatures?.includes('stripe') && (
      <div data-ui="connections-page-region-010" className="bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl p-6 shadow-inner-glow shadow-inner-glow-black-md border-none space-y-4">
        <div data-ui="connections-page-region-011" className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div data-ui="connections-page-region-012" className="flex items-start gap-4 flex-1 min-w-0">
            <div data-ui="connections-page-region-013" className="w-12 h-12 rounded-xl bg-indigo-500/20 text-indigo-300 flex items-center justify-center text-2xl shrink-0 border-none shadow-inner-glow shadow-inner-glow-white-sm">
              <FontAwesomeIcon icon={faStripe} className="icon-inner-glow-white" />
            </div>
            <div data-ui="connections-page-region-014" className="flex-1">
              <div data-ui="connections-page-region-015" className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-white">Stripe</h2>
                {stripeStatus?.connected && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${stripeStatus.active ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'}`}>
                    {stripeStatus.active ? t('connectionsPage.common.active', 'Activado') : t('connectionsPage.common.inactive', 'Desactivado')}
                  </span>
                )}
              </div>
              <p className="text-xs text-white/80 mt-1">
                {stripeStatus?.connected ? (
                  <>
                    {t('connectionsPage.stripe.connectedPrefix')} <span className="font-mono text-white/90">{stripeStatus.account?.id}</span>{' '}
                    ({t(`connectionsPage.stripe.mode.${stripeStatus.mode === 'live' ? 'live' : 'test'}`)})
                    {stripeStatus.account?.business_name ? ` — ${stripeStatus.account.business_name}` : ''}
                  </>
                ) : (
                  <>
                    <span className="block mb-1">{t('connectionsPage.stripe.pasteSecretHint')}</span>
                    <span className="block text-xs text-white/70">{t('connectionsPage.stripe.connectExpressHint', 'También puedes usar el flujo seguro de Stripe Connect a continuación.')}</span>
                  </>
                )}
              </p>

              {/* Stripe Connect actions */}
              <div data-ui="connections-page-region-016" className="mt-4 flex flex-wrap gap-2">
                <BtnTaratic
                  color="blue"
                  onClick={handleConnectWithStripe}
                  className="flex items-center gap-2 text-xs"
                >
                  <FontAwesomeIcon icon={faStripeS} />
                  <span>{t('connectionsPage.stripe.connectExpress', 'Conectar con Stripe (Express)')}</span>
                </BtnTaratic>
                <button
                  onClick={handleCheckConnectStatus}
                  className="border border-white/10 text-white/80 text-xs font-semibold py-2 px-3 rounded-xl hover:bg-white/5 transition-colors flex items-center gap-2"
                  title={t('connectionsPage.stripe.checkStatusTitle', 'Comprobar estado de Stripe')}
                >
                  <FontAwesomeIcon icon={faSync} /> <span>{t('connectionsPage.stripe.checkStatus', 'Comprobar estado')}</span>
                </button>
                <button
                  onClick={handleOpenStripeDashboard}
                  className="border border-blue-500/30 bg-blue-600/10 text-blue-300 text-xs font-semibold py-2 px-3 rounded-xl hover:bg-blue-600/20 hover:text-white transition-colors flex items-center gap-2"
                  title={t('connectionsPage.stripe.openDashboardTitle', 'Abrir Dashboard Express de Stripe')}
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> <span>{t('connectionsPage.stripe.openDashboard', 'Abrir Dashboard Stripe')}</span>
                </button>
              </div>

              {(!stripeStatus?.connected || showStripeChange) && (
                <div data-ui="connections-page-region-017" className="mt-4 pt-4 border-t border-white/10">
                  <label className="block text-xs font-medium text-white/80 mb-1.5">{t('connectionsPage.stripe.secretLabel')}</label>
                  <input
                    type="password"
                    className="w-full border border-white/10 bg-white/5 text-white placeholder-white/40 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                    placeholder={t('connectionsPage.stripe.secretPlaceholder')}
                    value={stripeSecret}
                    onChange={(e) => setStripeSecret(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          <div data-ui="connections-page-region-018" className="flex flex-wrap md:flex-nowrap items-center gap-2 md:justify-end w-full md:w-auto mt-3 md:mt-0">
            {stripeStatus?.connected ? (
              <>
                {!showStripeChange ? (
                  <>
                    <BtnTaratic
                      onClick={() => { setShowStripeChange(true); setStripeSecret(''); }}
                      className="flex items-center gap-2 text-xs"
                      title={t('connectionsPage.stripe.changeAccountTitle')}
                    >
                      <FontAwesomeIcon icon={faExchangeAlt} />
                      <span>{t('connectionsPage.common.change')}</span>
                    </BtnTaratic>
                    <BtnTaratic
                      color="red"
                      onClick={handleClearStripe}
                      disabled={disconnectingStripe}
                      className="flex items-center gap-2 text-xs"
                      title={t('connectionsPage.stripe.removeStripeTitle')}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span>{disconnectingStripe ? t('connectionsPage.common.removing') : t('connectionsPage.stripe.removeStripe')}</span>
                    </BtnTaratic>
                  </>
                ) : (
                  <>
                    <BtnTaratic
                      color="green"
                      onClick={handleSaveStripe}
                      disabled={savingStripe || !/^(sk_|rk_)/i.test(stripeSecret.trim())}
                      className="flex items-center gap-2 text-xs"
                      title={t('connectionsPage.stripe.saveNewKeyTitle')}
                    >
                      <FontAwesomeIcon icon={faSave} />
                      <span>{savingStripe ? t('connectionsPage.common.saving') : t('connectionsPage.common.save')}</span>
                    </BtnTaratic>
                    <button
                      onClick={() => { setShowStripeChange(false); setStripeSecret(''); }}
                      className="btn-taratic btn-ghost-dark border font-semibold py-2 px-3 text-xs rounded-xl transition-colors flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faTimes} /> {t('connectionsPage.common.cancel')}
                    </button>
                    <BtnTaratic
                      color="red"
                      onClick={handleClearStripe}
                      disabled={disconnectingStripe}
                      className="flex items-center gap-2 text-xs"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span>{disconnectingStripe ? t('connectionsPage.common.removing') : t('connectionsPage.stripe.removeStripe')}</span>
                    </BtnTaratic>
                  </>
                )}
              </>
            ) : (
              <BtnTaratic
                color="blue"
                onClick={handleSaveStripe}
                disabled={savingStripe || !/^(sk_|rk_)/i.test(stripeSecret.trim())}
                className="flex items-center gap-2 text-xs"
              >
                <FontAwesomeIcon icon={faLink} />
                <span>{savingStripe ? t('connectionsPage.common.saving') : t('connectionsPage.stripe.connectStripe')}</span>
              </BtnTaratic>
            )}
          </div>
        </div>

        {/* Stripe Enable/Disable Switch Row */}
        <div data-ui="connections-page-region-019" className="flex items-center justify-between pt-4 border-t border-white/10 mt-4">
          <div data-ui="connections-page-region-020">
            <span className="text-sm font-semibold text-white">{t('connectionsPage.stripe.gatewayTitle', 'Pasarela de Pago Stripe')}</span>
            <p className="text-xs text-white/70">{t('connectionsPage.stripe.gatewayDesc', 'Por defecto Stripe está desactivado. Actívalo para permitir pagos en línea.')}</p>
          </div>
          <div data-ui="connections-page-region-021" className="flex items-center gap-3">
            <span className={`text-xs font-bold ${stripeStatus?.active ? 'text-green-400' : 'text-gray-400'}`}>
              {stripeStatus?.active ? t('connectionsPage.common.active', 'Activado') : t('connectionsPage.common.inactive', 'Desactivado')}
            </span>
            <GhostSwitch
              checked={!!stripeStatus?.active}
              onChange={handleToggleStripeActive}
              disabled={togglingStripe}
              ariaLabel={t('connectionsPage.stripe.toggleAria', 'Activar o desactivar Stripe')}
            />
          </div>
        </div>
      </div>
      )}

      {/* Security & Access (2FA) Card */}
      <div data-ui="connections-page-region-022" className="bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl p-6 shadow-inner-glow shadow-inner-glow-black-md border-none space-y-4">
        <div data-ui="connections-page-region-023" className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div data-ui="connections-page-region-024" className="flex items-center gap-4 min-w-0 flex-1">
            <div data-ui="connections-page-region-025" className="w-12 h-12 rounded-xl bg-blue-600/30 text-blue-300 flex items-center justify-center text-2xl shrink-0 border-none shadow-inner-glow shadow-inner-glow-white-sm">
              <FontAwesomeIcon icon={faShieldAlt} className="icon-inner-glow-white" />
            </div>
            <div data-ui="connections-page-region-026">
              <h2 className="text-lg font-bold text-white">{t('connectionsPage.security.title', 'Seguridad y Acceso')}</h2>
              <p className="text-xs text-white/80 mt-1">
                {t('connectionsPage.security.subtitle', 'Doble Autenticación (2FA) vía correo electrónico.')}
              </p>
            </div>
          </div>
          <div data-ui="connections-page-region-027" className="flex items-center gap-3">
            <span className={`text-xs font-bold ${is2faEnabled ? 'text-green-400' : 'text-gray-400'}`}>
              {is2faEnabled ? t('connectionsPage.common.active', 'Activado') : t('connectionsPage.common.inactive', 'Desactivado')}
            </span>
            <GhostSwitch
              checked={is2faEnabled}
              onChange={handleToggle2FA}
              disabled={toggling2fa}
              ariaLabel={t('connectionsPage.security.ariaLabel', 'Doble Autenticación (2FA)')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionsPage;
